import { useEffect, useState } from 'react';
import { publicProfileDataSchema, type PublicProfileData } from '../features/public/public-types';

const productionOrigin = 'https://links.backyardcafekemer.com';
const maxProfileRendererWidth = 980;

type ProfileRendererModule = typeof import('../features/profile/PublicProfileRenderer');
type StudioRendererModule = typeof import('../features/studio/renderer');
type LoadedRenderer = {
  PublicProfileRenderer: ProfileRendererModule['PublicProfileRenderer'];
  parseStudioLayout: StudioRendererModule['parseStudioLayout'];
  deviceForWidth: StudioRendererModule['deviceForWidth'];
};

function setMeta(selector: string, attribute: string, value: string) {
  const element = document.head.querySelector<HTMLMetaElement>(selector);
  if (element) element.setAttribute(attribute, value);
}

function PublicLoading() {
  return (
    <main
      className="public-profile-page public-profile-page--loading"
      aria-label="Loading Backyard links"
    >
      <div className="public-profile-skeleton" role="status">
        <span className="skeleton skeleton--avatar" />
        <span className="skeleton public-profile-skeleton__name" />
        <span className="skeleton public-profile-skeleton__bio" />
        <span className="skeleton public-profile-skeleton__link" />
        <span className="skeleton public-profile-skeleton__link" />
        <span className="sr-only">Loading Backyard Cafe Kemer links…</span>
      </div>
    </main>
  );
}

function PublicError({ retry }: { retry: () => void }) {
  return (
    <main className="public-profile-page public-profile-page--error">
      <section className="public-error-card" role="alert">
        <span className="public-avatar public-avatar--fallback" aria-hidden="true">
          B
        </span>
        <h1>Backyard links are resting.</h1>
        <p>The profile is not available right now. Please try again in a moment.</p>
        <button type="button" onClick={retry}>
          Try again
        </button>
      </section>
    </main>
  );
}

export function PublicHomePage() {
  const [data, setData] = useState<PublicProfileData | null>(null);
  const [renderer, setRenderer] = useState<LoadedRenderer | null>(null);
  const [width, setWidth] = useState(() =>
    Math.min(document.documentElement.clientWidth, maxProfileRendererWidth),
  );
  const [error, setError] = useState(false);
  const [requestKey, setRequestKey] = useState(0);

  useEffect(() => {
    function onResize() {
      setWidth(Math.min(document.documentElement.clientWidth, maxProfileRendererWidth));
    }
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([
      import('../features/profile/PublicProfileRenderer'),
      import('../features/studio/renderer'),
    ])
      .then(([profileRenderer, studioRenderer]) => {
        if (active) {
          setRenderer({
            PublicProfileRenderer: profileRenderer.PublicProfileRenderer,
            parseStudioLayout: studioRenderer.parseStudioLayout,
            deviceForWidth: studioRenderer.deviceForWidth,
          });
        }
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void fetch('/api/public/profile', { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error('Profile unavailable');
        const next = publicProfileDataSchema.parse(await response.json());
        setData(next);
        document.title = `${next.profile.displayName} | Official Links`;
        setMeta(
          'meta[name="description"]',
          'content',
          next.profile.bio || 'Official Backyard Cafe Kemer links.',
        );
        setMeta(
          'meta[property="og:title"]',
          'content',
          `${next.profile.displayName} | Official Links`,
        );
        setMeta('meta[property="og:description"]', 'content', next.profile.bio);
        if (next.profile.avatarUrl) {
          const avatar = new URL(next.profile.avatarUrl, productionOrigin).toString();
          setMeta('meta[property="og:image"]', 'content', avatar);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => {
      controller.abort();
    };
  }, [requestKey]);

  if (error) {
    return (
      <PublicError
        retry={() => {
          setError(false);
          setData(null);
          setRequestKey((current) => current + 1);
        }}
      />
    );
  }
  if (!data || !renderer) return <PublicLoading />;

  return (
    <renderer.PublicProfileRenderer
      profile={data.profile}
      links={data.links}
      appearance={data.appearance}
      studioLayout={renderer.parseStudioLayout(data.layout)}
      width={width}
      deviceMode={renderer.deviceForWidth(width)}
      renderMode="public"
      interactive
      trackAnalytics
      className="public-profile-renderer"
    />
  );
}
